/**
 * Helpers for list-page search + filter + sort wiring used together with
 * `MainTabsTableHeader`.
 *
 * Pattern per list page:
 *
 *   const [searchValue, setSearchValue] = useState("")
 *   const [sortValue, setSortValue] = useState("default")
 *   const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({})
 *
 *   const visibleRows = useMemo(() => {
 *     let rows = applyFilters(allRows, selectedFilters, FILTER_ACCESSORS)
 *     rows = applySearch(rows, searchValue, SEARCH_FIELDS)
 *     rows = applySort(rows, sortValue, SORTERS)
 *     return rows
 *   }, [allRows, selectedFilters, searchValue, sortValue])
 */

// ----- Filter accessors -----

/** Map of filter key → function that reads the value(s) to compare from one row.
 *  Return a single value for one-to-one columns (e.g. status) or an array of
 *  values for many-per-row columns (e.g. cities_covered = ["Hanoi","HCM"]). */
export type FilterAccessor<T> = (
  row: T,
) => string | number | (string | number)[] | null | undefined

export type FilterAccessors<T> = Record<string, FilterAccessor<T>>

/** Multi-select filter: row passes a group if ANY of its cell values matches
 *  ANY selected value; row must pass EVERY active group.
 *  Empty group arrays mean "no constraint". */
export function applyFilters<T>(
  rows: T[],
  selected: Record<string, string[]>,
  accessors: FilterAccessors<T>,
): T[] {
  const activeEntries = Object.entries(selected).filter(([, vals]) => vals && vals.length > 0)
  if (activeEntries.length === 0) return rows
  return rows.filter((row) =>
    activeEntries.every(([key, vals]) => {
      const accessor = accessors[key]
      if (!accessor) return true
      const raw = accessor(row)
      if (raw == null) return false
      const cellValues = Array.isArray(raw)
        ? raw.map((v) => String(v).trim())
        : [String(raw).trim()]
      return cellValues.some((cv) => vals.includes(cv))
    }),
  )
}

// ----- Search -----

/** Case-insensitive substring search across any number of accessor functions.
 *  Drops rows that match no field. Empty query → returns input unchanged. */
export function applySearch<T>(
  rows: T[],
  query: string,
  fields: ((row: T) => string | number | null | undefined)[],
): T[] {
  const q = query.trim().toLowerCase()
  if (!q) return rows
  return rows.filter((row) =>
    fields.some((fn) => String(fn(row) ?? "").toLowerCase().includes(q)),
  )
}

// ----- Sort -----

export type RowSorter<T> = (a: T, b: T) => number

/** Apply a named sorter from a map. "default" or unknown values → no sort. */
export function applySort<T>(
  rows: T[],
  sortValue: string,
  sorters: Record<string, RowSorter<T>>,
): T[] {
  const fn = sorters[sortValue]
  return fn ? [...rows].sort(fn) : rows
}

// ----- Unique-value extraction (for filter group options) -----

/** Extract distinct non-empty string values from rows via an accessor.
 *  Accessor may return a single value or an array (auto-flattened).
 *  Returns `{label, value}[]` sorted alphabetically by default. */
export function uniqueOptionsFromRows<T>(
  rows: T[],
  accessor: (row: T) => string | number | (string | number)[] | null | undefined,
  options?: { sort?: boolean; format?: (v: string) => string },
): { label: string; value: string }[] {
  const sort = options?.sort !== false
  const format = options?.format
  const set = new Set<string>()
  rows.forEach((r) => {
    const raw = accessor(r)
    if (raw == null) return
    const list = Array.isArray(raw) ? raw : [raw]
    list.forEach((item) => {
      const v = String(item).trim()
      if (v) set.add(v)
    })
  })
  const out = Array.from(set).map((v) => ({ label: format ? format(v) : v, value: v }))
  if (sort) out.sort((a, b) => a.label.localeCompare(b.label))
  return out
}

// ----- Column-header sort (3-state cycle: asc → desc → null) -----

export type SortDirection = "asc" | "desc" | null

export interface SortState {
  key: string | null
  direction: SortDirection
}

export const initialSortState: SortState = { key: null, direction: null }

/** 3-state cycle for clicking a column header. First click on a new key → asc.
 *  Clicking same key cycles asc → desc → cleared (null). Per spec. */
export const cycleSort = (prev: SortState, key: string): SortState => {
  if (prev.key !== key) return { key, direction: "asc" }
  if (prev.direction === "asc") return { key, direction: "desc" }
  return { key: null, direction: null }
}

/** Compare two cell values. Detects numbers and ISO/date-like strings; falls back to
 *  case-insensitive locale string compare. Nulls / undefined / empty strings sink to bottom. */
export const compareValues = (a: unknown, b: unknown): number => {
  const isNil = (v: unknown) => v == null || v === ""
  if (isNil(a) && isNil(b)) return 0
  if (isNil(a)) return 1
  if (isNil(b)) return -1

  if (typeof a === "number" && typeof b === "number") return a - b

  // Date-like strings: must contain a 4-digit year AND parse to valid date.
  const aStr = String(a)
  const bStr = String(b)
  if (/\d{4}/.test(aStr) && /\d{4}/.test(bStr)) {
    const ad = Date.parse(aStr)
    const bd = Date.parse(bStr)
    if (!Number.isNaN(ad) && !Number.isNaN(bd)) return ad - bd
  }

  return aStr.toLowerCase().localeCompare(bStr.toLowerCase())
}

/** Sort rows by current column-header state. Returns input unchanged when no sort active.
 *  `accessors` lets pages override the cell value (e.g. for nested/composite fields);
 *  default reads `row[key]`. */
export function sortRowsBy<T>(
  rows: T[],
  sort: SortState,
  accessors?: Record<string, (row: T) => unknown>,
): T[] {
  if (!sort.key || !sort.direction) return rows
  const key = sort.key
  const getter = accessors?.[key] ?? ((row: T) => (row as unknown as Record<string, unknown>)[key])
  const dir = sort.direction === "asc" ? 1 : -1
  return [...rows].sort((a, b) => compareValues(getter(a), getter(b)) * dir)
}

// ----- Common name comparators -----

export const compareByLocale = (a: string, b: string): number =>
  a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase())

export const sortByNameAsc =
  <T>(getName: (row: T) => string | null | undefined): RowSorter<T> =>
  (a, b) =>
    compareByLocale(String(getName(a) ?? ""), String(getName(b) ?? ""))

export const sortByNumberAsc =
  <T>(getValue: (row: T) => number | null | undefined): RowSorter<T> =>
  (a, b) =>
    (Number(getValue(a)) || 0) - (Number(getValue(b)) || 0)

export const sortByNumberDesc =
  <T>(getValue: (row: T) => number | null | undefined): RowSorter<T> =>
  (a, b) =>
    (Number(getValue(b)) || 0) - (Number(getValue(a)) || 0)
